/** NikahElegant — Hero. Bismillah calligraphy with animated geometric mandala. */

import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, MapPin, ChevronDown } from 'lucide-react';
import { NIKAN_EASE } from '../theme';
import type { NikahContent } from '../content';

// Rotating geometric mandala (Islamic star pattern, CSS-only)
function Mandala() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-30">
      <motion.div
        className="h-[140vmin] w-[140vmin] rounded-full"
        style={{
          background:
            'conic-gradient(from 0deg, transparent 0deg, rgba(201,162,39,0.25) 30deg, transparent 60deg, rgba(15,107,79,0.25) 90deg, transparent 120deg, rgba(201,162,39,0.25) 150deg, transparent 180deg, rgba(15,107,79,0.25) 210deg, transparent 240deg, rgba(201,162,39,0.25) 270deg, transparent 300deg, rgba(15,107,79,0.25) 330deg, transparent 360deg)',
          maskImage: 'radial-gradient(circle, black 30%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(circle, black 30%, transparent 70%)',
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
}

export function HeroSection({
  content,
  onRsvp,
}: {
  content: NikahContent['hero'];
  onRsvp: () => void;
}) {
  return (
    <header
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 py-20 text-center"
      style={{
        background:
          'linear-gradient(160deg, var(--nikah-emerald-dark) 0%, var(--nikah-emerald) 55%, var(--nikah-emerald-dark) 100%)',
        color: 'var(--nikah-ivory)',
      }}
    >
      <Mandala />

      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: NIKAN_EASE }}
        className="mb-6 font-serif text-2xl tracking-wide text-[var(--nikah-gold-soft)] sm:text-3xl"
        dir="rtl"
      >
        {content ? 'بسم الله الرحمن الرحيم' : ''}
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1, ease: NIKAN_EASE }}
        className="mb-3 text-sm uppercase tracking-[0.3em] text-[var(--nikah-gold)]"
      >
        {content.kicker}
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: NIKAN_EASE }}
        className="font-serif text-5xl font-semibold leading-tight text-[var(--nikah-ivory)] sm:text-7xl"
      >
        {content.title}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.35, ease: NIKAN_EASE }}
        className="mt-4 text-lg italic text-[var(--nikah-gold-soft)] sm:text-xl"
      >
        {content.subtitle}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.5, ease: NIKAN_EASE }}
        className="mt-10 flex flex-col items-center gap-3 text-sm text-[var(--nikah-ivory)]/90 sm:flex-row sm:gap-8"
      >
        <span className="inline-flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-[var(--nikah-gold)]" />
          {content.dateLabel}
        </span>
        <span className="inline-flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[var(--nikah-gold)]" />
          {content.venueShort}
        </span>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.65 }}
        className="mt-8 text-sm text-[var(--nikah-ivory)]/70"
      >
        Hosted by {content.host}
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        onClick={onRsvp}
        className="mt-10 rounded-full bg-[var(--nikah-gold)] px-8 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--nikah-emerald-dark)] shadow-lg transition-transform hover:scale-105"
      >
        RSVP
      </motion.button>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 1 }}
        className="absolute bottom-6"
      >
        <ChevronDown className="h-6 w-6 animate-bounce text-[var(--nikah-gold)]" />
      </motion.div>
    </header>
  );
}
